import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { LIMIT_PER_PAGE } from "../../Common/Constants";
import { debounce } from "../../Common/debounce";
import { Loader } from "../../Common/Loader";
import { Pagination } from "../../Common/Pagination";
import { Table } from "../../Common/Table";
import useDebounce from "../../Common/useDebounce";
import {
  setPage,
  setTotalItems,
  setTotalPages,
} from "../../Redux/Slice/Common/PaginationSlice";
import { getPlanTypeList } from "../../Redux/Slice/PlanType/GetPlanTypeListSlice";
import PlusIcon from "./../../Assets/svgs/PlusIcon.";
import { dateFormat } from "../../Common/DateAndTimeFormat";
import DeletePlanTypeModal from "../../Model/DeletePlanTypeModal";
import { ConfirmModel } from "../../Model/ConfirmModel";
import { deletePlanType } from "../../Redux/Slice/PlanType/DeletePlanTypeSlice";
import useSuccessErrorAlert from "../../Hooks/useSuccessErrorAlert";
import { SuccessModel } from "../../Model/SuccessModel";
import { ErrorModel } from "../../Model/ErrorModel";

const PlanTypeList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const searchQuery = useDebounce(searchText, 500);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDeletePlanType, setConfirmDeletePlanType] = useState({
    visible: false,
    selectedPlanType: null,
  });
  const { successModal, showSuccessMessage, errorModal, showErrorMessage } =
    useSuccessErrorAlert();
  const [localItemsPerPage, setLocalItemsPerPage] = useState(LIMIT_PER_PAGE);
  const [deletePlanTypeModal, setDeletePlanTypeModal] = useState({
    visible: false,
    selectedPlanType: null,
  });

  const planTypeListData = useSelector(
    (state) => state.planTypeList.planTypeListData
  );

  const formattedPlanTypeList = useMemo(() => {
    if (Array.isArray(planTypeListData?.data))
      return planTypeListData?.data?.map((planType) => ({
        ...planType,
        createdAt: dateFormat(planType?.createdAt),
      }));
    return [];
  }, [planTypeListData]);

  const getPlansTypeList = async (page = 1, limit) => {
    setIsLoading(true);
    const result = await dispatch(getPlanTypeList({ searchText, page, limit }));
    await dispatch(setPage(result?.payload?.pagination?.currentPage));
    await dispatch(setTotalItems(result?.payload?.pagination?.totalItems));
    await dispatch(setTotalPages(result?.payload?.pagination?.totalPages));
    setIsLoading(false);
  };

  const onDeletePlanType = async () => {
    const selectedPlanType = confirmDeletePlanType?.selectedPlanType;
    const planTypeId = selectedPlanType?.id;
    setConfirmDeletePlanType((prev) => ({
      ...prev,
      visible: false,
      selectedPlanType: null,
    }));
    setIsLoading(true);
    const result = await dispatch(deletePlanType(planTypeId));
    setIsLoading(false);
    if (
      deletePlanType.fulfilled.match(result) &&
      result?.payload?.success === true
    ) {
      showSuccessMessage("Plan Type deleted successfully", getPlansTypeList);
    } else if (result?.payload?.data?.isAlreadyUsed) {
      setDeletePlanTypeModal({
        visible: true,
        selectedPlanType,
      });
    } else {
      showErrorMessage(result?.payload?.data?.message);
    }
  };

  const onConfirmDeletePlanType = (selectedPlanType) => {
    setConfirmDeletePlanType({
      visible: true,
      selectedPlanType,
    });
  };

  const onCloseConfirmDeletePlanType = () => {
    setConfirmDeletePlanType({
      visible: false,
      selectedPlanType: null,
    });
  };

  const onCloseDeletePlanType = () => {
    setDeletePlanTypeModal({
      visible: false,
      selectedPlanType: null,
    });
  };

  const onSuccessDeletePlanType = () => {
    onCloseDeletePlanType();
    getPlansTypeList();
  };

  const debouncedGetUserList = useCallback(debounce(getPlansTypeList, 300), [
    searchQuery,
  ]);

  useEffect(() => {
    debouncedGetUserList(1, localItemsPerPage);
  }, [searchQuery, debouncedGetUserList]);

  const onEdit = (row) => {
    navigate("/edit-plan-type/" + row.id);
  };

  const columns = [
    {
      label: "Plan Type",
      key: "type",
      className: "profile",
    },
    {
      label: "No of Assistant",
      key: "noOfAssistants",
      className: "cellText",
    },
    {
      label: "Creation Date",
      key: "createdAt",
      className: "cellText",
    },
  ];

  return (
    <>
      {isLoading ? <Loader /> : ""}
      {deletePlanTypeModal.visible ? (
        <DeletePlanTypeModal
          onClose={onCloseDeletePlanType}
          onSuccess={onSuccessDeletePlanType}
          selectedPlanTypeForDeletion={deletePlanTypeModal.selectedPlanType}
        />
      ) : null}
      {confirmDeletePlanType?.visible ? (
        <ConfirmModel
          onAccept={onDeletePlanType}
          onReject={onCloseConfirmDeletePlanType}
          yes={"Yes"}
          no={"No"}
          title="Remove Plan Type!"
          message="Are you sure you want to remove the plan type?"
        />
      ) : null}
      {successModal?.visible ? (
        <SuccessModel message={successModal.message} />
      ) : null}
      {errorModal?.visible ? <ErrorModel message={errorModal.message} /> : null}

      <div className="page__body">
        <div className="tableWrap">
          <div className="tableWrap__head tableWrap__head--wrap">
            <div className="formRow m_0  formRow--flexEnd">
              <div className="formCol formCol--flexInput">
                <div className="formCol flexField">
                  <input
                    className="form__input form__input--search"
                    type="text"
                    placeholder="Search a Plan Type"
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value.trimStart());
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="btn__group">
              <button
                className="btn btn__primary"
                onClick={() => {
                  navigate("/add-plan-type");
                }}
              >
                <PlusIcon color="#fff" />
                Add Plan Type
              </button>
            </div>
          </div>
          <Table
            columns={columns}
            data={formattedPlanTypeList}
            onEdit={onEdit}
            onDelete={onConfirmDeletePlanType}
            hasAction="true"
          />
        </div>
      </div>
      <Pagination
        apiMethod={getPlansTypeList}
        itemsPerPage={localItemsPerPage}
        setItemsPerPage={setLocalItemsPerPage}
      />
    </>
  );
};

export default PlanTypeList;
